class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// Desktop hover behaviour for the header/mega menus.
const HEADER_MENU_HOVER_QUERY = '(min-width: 1024px) and (hover: hover) and (pointer: fine)';
const HEADER_MENU_OPEN_DELAY = 100;
const HEADER_MENU_CLOSE_DELAY = 220;

class HeaderMenu extends DetailsDisclosure {
  static openInstance = null;

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.openTimer = null;
    this.closeTimer = null;
    this.hoverMedia = window.matchMedia(HEADER_MENU_HOVER_QUERY);

    this.parentLink = this.summary.querySelector('.header__menu-item-link');

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('focusin', this.onFocusIn.bind(this));
  }

  get hoverEnabled() {
    return this.hoverMedia.matches;
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openTimer = null;
    this.closeTimer = null;
  }

  onMouseEnter() {
    if (!this.hoverEnabled) return;
    this.clearTimers();
    if (this.mainDetailsToggle.open) return;

    // Switch instantly when another menu is already showing, otherwise wait a
    // beat so sweeping the cursor across the nav doesn't fire menus open.
    const delay = HeaderMenu.openInstance && HeaderMenu.openInstance !== this ? 0 : HEADER_MENU_OPEN_DELAY;
    this.openTimer = setTimeout(() => this.open(), delay);
  }

  onMouseLeave() {
    if (!this.hoverEnabled) return;
    this.clearTimers();
    // Grace period so crossing the gap between summary and panel doesn't flicker.
    this.closeTimer = setTimeout(() => this.close(), HEADER_MENU_CLOSE_DELAY);
  }

  onSummaryClick(event) {
    const onParentLink = this.parentLink && this.parentLink.contains(event.target);

    if (this.hoverEnabled) {
      // The panel is already open from hover, so let the parent link navigate.
      if (onParentLink) return;
      event.preventDefault();
      if (!this.mainDetailsToggle.open) this.open();
      return;
    }

    // Touch/no-hover: first tap reveals the menu, a second tap follows the link.
    event.preventDefault();
    if (this.mainDetailsToggle.open) {
      if (onParentLink && this.parentLink.getAttribute('href')) {
        window.location.href = this.parentLink.href;
      } else {
        this.close();
      }
    } else {
      this.open();
    }
  }

  onFocusIn(event) {
    // Keyboard users get the panel revealed as they reach the top-level control.
    if (!this.hoverEnabled) return;
    if (event.target === (this.parentLink || this.summary)) {
      this.clearTimers();
      this.open();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.mainDetailsToggle.open) {
      this.clearTimers();
      this.close();
      this.summary.focus();
      return;
    }

    // With a parent link, Enter navigates natively and Space toggles the panel.
    // Without one the summary is the focusable control, so both keys toggle.
    const control = this.parentLink || this.summary;
    if (event.target !== control) return;

    const toggleKeys = this.parentLink ? [' '] : [' ', 'Enter'];
    if (toggleKeys.includes(event.key)) {
      event.preventDefault();
      this.clearTimers();
      if (this.mainDetailsToggle.open) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  open() {
    if (HeaderMenu.openInstance && HeaderMenu.openInstance !== this) {
      HeaderMenu.openInstance.clearTimers();
      HeaderMenu.openInstance.close();
    }
    HeaderMenu.openInstance = this;
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
  }

  close() {
    if (HeaderMenu.openInstance === this) HeaderMenu.openInstance = null;
    super.close();
  }

  onFocusOut() {
    // On desktop the pointer, not focus, decides when the menu closes.
    if (this.hoverEnabled) return;
    super.onFocusOut();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
